/**
 * Playbook glossary + BrightLabs self-audit
 *
 * Two datasets consumed by the Delivery Playbooks screen:
 *
 *   1. GLOSSARY  — plain-English explanations of jargon that appears across
 *                  the playbooks. Each entry gives you a short "what it is"
 *                  paragraph and a one-line "how to explain it to a client".
 *                  The AdminDeliveryPlaybooks view auto-scans text for these
 *                  terms and wraps them in a hoverable popover.
 *
 *   2. SELF_AUDIT — a per-checklist-item honest assessment of whether
 *                   brightlabs.live itself does this. Shown as a small
 *                   status pill next to every Websites checklist item so
 *                   we can say "yes, we do this on our own site" or spot
 *                   the gaps.
 *
 * Keep the tone conversational — this is for a non-technical operator
 * to explain to a non-technical client. Precision second, clarity first.
 */

// ---------------------------------------------------------------------------
// 1. Glossary
// ---------------------------------------------------------------------------
// Keys are lower-case, the matching component preserves original casing.
// Multi-word terms are supported and get preference over single words.

export const GLOSSARY = {
  'ga4': {
    label: 'GA4',
    plain:
      'Google Analytics 4 — the current version of Google’s free traffic-tracking tool. It records who visits, what page they land on, which pages they read, and which of them do something valuable (fill a form, click call, buy).',
    tip:
      'Say: "GA4 is Google’s tool for seeing how people use your site — it replaced Universal Analytics in 2023."',
  },
  'server-side conversion tracking': {
    label: 'Server-side conversion tracking',
    plain:
      'Instead of relying on the browser to fire tracking events, we fire them from our own server. That means iOS privacy blocks, ad-blockers and cookie consent tools don’t silently break your reporting.',
    tip:
      'Say: "It’s a more reliable way of counting real leads — the numbers actually match what lands in the inbox."',
  },
  '301 map': {
    label: '301 map',
    plain:
      'A spreadsheet listing every old URL on your website and where each one should now point. A "301" is a permanent redirect — it tells Google the old page has moved so you keep the SEO value.',
    tip:
      'Say: "Without a 301 map, launching a new site is like moving house without telling anyone — Google can’t find you at the new address."',
  },
  '301 redirect': {
    label: '301 redirect',
    plain:
      'A permanent forwarding instruction. Anyone (or Googlebot) who visits the old URL gets sent to the new one, and Google passes the old page’s ranking authority to the new page.',
    tip:
      'Say: "It’s a forwarding address so you don’t lose your Google ranking when you change a page URL."',
  },
  'canonical': {
    label: 'Canonical URL',
    plain:
      'A tag in the page code that tells Google "this is the official version of this page". Stops duplicate-content problems when the same page can be reached at multiple URLs (with/without www, with query strings, etc.).',
    tip:
      'Say: "It’s a note to Google saying which URL is the real one for each page — prevents them getting confused about duplicates."',
  },
  'hreflang': {
    label: 'hreflang',
    plain:
      'A tag telling Google which language/region a page targets, so it can show UK visitors the /en-gb version and Indian visitors the /en-in version.',
    tip:
      'Say: "It’s how we tell Google which language and country each page is for."',
  },
  'core web vitals': {
    label: 'Core Web Vitals',
    plain:
      'Google’s three-point speed test for websites: how quickly the biggest thing on the page loads (LCP), how stable the layout is while loading (CLS), and how quickly it responds when you click (INP). Green scores are a small Google ranking factor.',
    tip:
      'Say: "It’s Google’s scorecard for how quick and stable the site feels — we ship every launch in the green zone."',
  },
  'lcp': {
    label: 'LCP',
    plain:
      'Largest Contentful Paint — how long it takes for the biggest visible element on a page (usually the hero image) to fully render. Target: under 2.5 seconds.',
    tip:
      'Say: "It’s how quickly the main image or headline appears — under 2.5 seconds is the target."',
  },
  'cls': {
    label: 'CLS',
    plain:
      'Cumulative Layout Shift — how much the page jumps around while loading. Everyone hates when you’re about to tap something and the layout shifts and you tap the wrong thing. Target: under 0.1.',
    tip:
      'Say: "It measures how much the page jumps around while loading — we keep it near zero."',
  },
  'inp': {
    label: 'INP',
    plain:
      'Interaction to Next Paint — how quickly the page responds when a user taps or clicks. Replaced First Input Delay in 2024. Target: under 200ms.',
    tip:
      'Say: "It’s how snappy the page feels when you click something."',
  },
  'structured data': {
    label: 'Structured data',
    plain:
      'A hidden piece of code (in a format called schema.org) that describes what a page is about in a machine-readable way. Lets Google show rich results — star ratings, prices, FAQs, opening hours — directly in search.',
    tip:
      'Say: "It’s hidden labels on the page that help Google show extra info in search results — like star ratings, prices, or opening hours."',
  },
  'schema': {
    label: 'Schema (schema.org)',
    plain:
      'The standardised vocabulary used inside structured data. Common types: Organization (info about your business), Service (what you offer), FAQPage (Q&A), LocalBusiness (address + opening hours), BreadcrumbList (page hierarchy).',
    tip:
      'Say: "It’s a common language every search engine agreed to — we use it to describe your business, services and FAQs to Google."',
  },
  'sitemap.xml': {
    label: 'sitemap.xml',
    plain:
      'A machine-readable list of every URL on your site, submitted to Google Search Console so Google knows what to crawl and when it was last updated.',
    tip:
      'Say: "It’s the map of your site we hand to Google so it doesn’t miss any pages."',
  },
  'robots.txt': {
    label: 'robots.txt',
    plain:
      'A tiny text file at the root of your site telling search engines which pages they can and can’t crawl — usually used to block admin, staging or login pages from being indexed.',
    tip:
      'Say: "It’s the rulebook for search engines — tells them which parts of the site are off-limits."',
  },
  'on-page seo': {
    label: 'On-page SEO',
    plain:
      'Everything on the page itself that helps it rank: title tag, meta description, heading structure (H1/H2/H3), image alt text, internal links, readable URLs.',
    tip:
      'Say: "It’s the SEO work you can see and edit on the page — headings, page titles, image descriptions."',
  },
  'meta description': {
    label: 'Meta description',
    plain:
      'The 1–2 sentence blurb that appears under your title in Google search results. It doesn’t affect ranking directly but heavily affects whether someone actually clicks.',
    tip:
      'Say: "It’s the little summary line Google shows under your page title — it’s your Google shop-window."',
  },
  'alt text': {
    label: 'Alt text',
    plain:
      'A short written description of an image, coded into the page. Screen readers read it aloud for blind users; Google uses it to understand the image. Also shows if the image fails to load.',
    tip:
      'Say: "It’s a written description of every image, so blind users and Google both understand what’s in the picture."',
  },
  'image alt': {
    label: 'Alt text (image alt)',
    plain:
      'A short written description of an image. Accessibility for screen readers + a small SEO signal for Google image search.',
    tip:
      'Say: "It’s the written description of each image on the page — so screen readers can read it aloud, and Google can rank it in image search."',
  },
  'cms': {
    label: 'CMS',
    plain:
      'Content Management System — the back-end interface that lets you edit page content yourself without touching code. Examples: WordPress, Framer, Webflow, Contentful, a bespoke admin panel.',
    tip:
      'Say: "It’s the editor screen where you can update your website copy yourself — no dev needed for content changes."',
  },
  'crm': {
    label: 'CRM',
    plain:
      'Customer Relationship Management — the system that holds every enquiry, contact, lead status and follow-up. Examples: HubSpot, Pipedrive, or a bespoke build.',
    tip:
      'Say: "It’s where every enquiry, contact and pipeline stage lives — the brain of your sales operation."',
  },
  'staging': {
    label: 'Staging',
    plain:
      'A copy of the live website hosted on a hidden URL where you can preview changes and test everything works — before pushing it live to the real world.',
    tip:
      'Say: "It’s the dress-rehearsal version of the site — you can click around and check everything before we push it live."',
  },
  'ppc': {
    label: 'PPC',
    plain:
      'Pay-Per-Click advertising — the model behind Google Ads, Meta Ads, LinkedIn Ads. You only pay when someone clicks your ad, not when it’s shown.',
    tip:
      'Say: "PPC just means paid ads on Google or Meta — you only pay when someone actually clicks."',
  },
  'attribution': {
    label: 'Attribution',
    plain:
      'The system for deciding which marketing channel deserves credit for a lead or sale. Was it the Google Ad? The organic search? The LinkedIn post? Attribution models let you compare fairly.',
    tip:
      'Say: "It’s how you fairly credit each marketing channel for the leads it creates — so you know where your money is really working."',
  },
  'ia': {
    label: 'IA (Information Architecture)',
    plain:
      'The plan of what pages exist and how they’re grouped — the site’s skeleton. Comes before design, because the design has to serve the structure.',
    tip:
      'Say: "It’s the site’s skeleton — what pages exist and how they’re organised — before we design anything."',
  },
  'information architecture': {
    label: 'Information Architecture',
    plain:
      'The plan of what pages exist and how they’re grouped. Comes before wireframes and design.',
    tip:
      'Say: "It’s the site’s skeleton — what pages exist and how they connect."',
  },
  'wireframes': {
    label: 'Wireframes',
    plain:
      'Grey-box sketches of each page showing where content, buttons and images will sit — no colours, no fonts, no images. Cheap to change now, expensive later.',
    tip:
      'Say: "They’re the grey-box layouts we sign off before we design — quicker to change now than after."',
  },
  'positioning': {
    label: 'Positioning',
    plain:
      'What your business stands for, who it’s for, and why it’s different from the alternatives — in one sentence. Underlies every page, ad and pitch.',
    tip:
      'Say: "It’s the one-line answer to who you serve, what you do, and why you — everything else follows from that."',
  },
  'backup + rollback': {
    label: 'Backup + rollback',
    plain:
      'A saved snapshot of the site plus a documented process to revert to it in minutes if a deploy breaks something. Non-negotiable before any launch.',
    tip:
      'Say: "It’s an undo button for the whole site — if a launch breaks anything, we can be back to the last working version in minutes."',
  },
  'landing page': {
    label: 'Landing page',
    plain:
      'A single-purpose page built to convert visitors from a specific ad campaign or channel. Not the homepage — a dedicated page for one offer, one audience, one action.',
    tip:
      'Say: "It’s the dedicated page an ad sends people to — one offer, one action, no distractions."',
  },
  'funnel': {
    label: 'Funnel',
    plain:
      'The journey from stranger → aware → interested → enquiry → customer. Marketers use it to see where people drop off so they can fix the leak instead of pouring more traffic in.',
    tip:
      'Say: "It’s the shape of the customer journey — where they enter, where most people drop off, and where the money is made."',
  },
  'notification rules': {
    label: 'Notification rules',
    plain:
      'Who on the team gets emailed/Slacked/SMS’d when a specific event happens — new lead, high-value form, cancelled booking. Set once, saves people missing enquiries.',
    tip:
      'Say: "The rules that decide who gets pinged when — new lead, booking, cancellation — so nothing slips through."',
  },
  'discovery': {
    label: 'Discovery',
    plain:
      'A structured 60–90 min conversation at the start of any engagement where we surface the real goal, the constraints, the numbers and the risks before we quote or scope.',
    tip:
      'Say: "It’s the deep-dive call before any quote — where we get honest about what actually needs solving."',
  },
  'form → crm': {
    label: 'Form → CRM',
    plain:
      'The wire that connects the website’s contact form to the CRM. Every enquiry lands with the right tags, source and follow-up rules — instead of just an email that gets lost.',
    tip:
      'Say: "It’s the pipe between the form on the site and the CRM — enquiries land tagged and ready to action, not stuck in someone’s inbox."',
  },
  'organization schema': {
    label: 'Organization schema',
    plain:
      'A specific type of structured data describing your business itself — name, logo, address, phone, social profiles. Powers the knowledge panel on the right of Google search.',
    tip:
      'Say: "It’s the code that tells Google about your business — name, address, phone — so it can build your Google profile."',
  },
  'breadcrumblist': {
    label: 'BreadcrumbList',
    plain:
      'Structured data describing a page’s position in the site hierarchy (Home → Services → SEO). Lets Google show the trail in search results instead of a raw URL.',
    tip:
      'Say: "It’s the little trail Google can show under your search result — Home › Services › SEO — instead of a messy URL."',
  },
};

// ---------------------------------------------------------------------------
// 2. BrightLabs website self-audit
// ---------------------------------------------------------------------------
// Keyed to the exact checklist string in deliveryPlaybooks.js.websites.checklist.
// Statuses: 'yes' | 'partial' | 'no' | 'na'
// Update these honestly. This is what we say when a client asks "do you do
// all this on your own site?"

export const SELF_AUDIT = {
  websites: {
    'Discovery call recorded + notes filed against the client record.': {
      status: 'na',
      note: 'Client-facing step. Not relevant to our own marketing site.',
    },
    'Positioning statement approved by client in writing before design starts.': {
      status: 'yes',
      note: 'Locked as "Creative · Technology · Growth · Automate" during V2 rebuild. Written across `/about` and homepage.',
    },
    'Sitemap signed off before wireframes.': {
      status: 'yes',
      note: 'V2 sitemap approved before any pages were designed — evidenced by `/public/sitemap.xml` and `/insights` library.',
    },
    'Wireframes signed off before visual design.': {
      status: 'yes',
      note: 'Every V2 page went through wireframe + copy before the visual pass.',
    },
    'GA4 + server-side conversion tracking installed pre-launch.': {
      status: 'yes',
      note: 'Feb 17 2026 — LIVE. GA4 client-side (G-8Y5QWLMQN8) + server-side Measurement Protocol both firing `generate_lead` on every /api/enquiries submission with per-source lead value (GBP). Validated end-to-end with Google\'s debug endpoint (validationMessages: []) and confirmed in GA4 DebugView. Wired into /start, DiscoveryModal, GrowthScore and AuditModal. See /app/memory/ga4-server-side-activation.md.',
    },
    '301 map completed for every URL that changes.': {
      status: 'partial',
      note: 'V2 launched with same routes as V1 for the marketing surface. Recommend a spot-check for /packages/*, /training/* redirects to catch legacy inbound links.',
    },
    'Core Web Vitals green on staging.': {
      status: 'partial',
      note: 'Feb 17 2026 — THREE perf sprints shipped. Combined result: main.js 552→278KB (50%), main.css 37→25KB gzip after PurgeCSS strip (33%), Fraunces subset to only used glyphs (118KB→~5KB), gtag.js deferred to window.load (removes 164KB blocking script from critical path), hydration mismatch fixed, monogram+case shots→WebP. Mobile improvements pending final production redeploy. Third-party overhead (Emergent PostHog+array.js+Google Tag Manager combined ~350KB) is a hard floor imposed by the platform — the practical ceiling on mobile Lighthouse is ~85-88.',
    },
    'All form submissions test-fired into CRM before launch.': {
      status: 'yes',
      note: '`/start` and `/contact` forms both POST to `/api/enquiries/`, land in the CRM Enquiries screen. Verified in testing.',
    },
    'Notification rules confirmed with client (who gets emailed when).': {
      status: 'yes',
      note: 'Enquiry alerts routed to hello@brightlabs.live. Configured in admin email settings.',
    },
    'On-page SEO pass on every page — title, meta, H1, image alt.': {
      status: 'yes',
      note: 'Fixed Feb 17, 2026 — Node prerender writes unique title/meta/canonical/H1 across all 43 routes. Verified in production.',
    },
    'Structured data (Organization + Service + BreadcrumbList) validated.': {
      status: 'yes',
      note: 'Feb 17 2026 audit — every page carries valid Organization, LocalBusiness, WebSite+SearchAction, OfferCatalog (6 services + prices), FAQPage, ContactPoint. BreadcrumbList now injected per-route at build time (e.g. Home › Norwich › SEO). Zero missing required fields across 9 sampled URLs. See /app/memory/schema-audit-2026-02-17.md.',
    },
    'sitemap.xml + robots.txt reviewed.': {
      status: 'yes',
      note: 'Both live. robots.txt blocks /admin, /login, /training as expected. sitemap.xml lists 38 marketing URLs.',
    },
    'Backup + rollback plan documented.': {
      status: 'yes',
      note: 'Emergent platform rollback + GitHub source of truth. Any deploy can be reverted from the chat interface within 60s.',
    },
    'Client handover: CMS training call + written editing guide.': {
      status: 'na',
      note: 'We edit our own site. Not applicable.',
    },
    'Post-launch 14-day monitoring window in the sprint plan.': {
      status: 'partial',
      note: 'Informally monitored. Recommend formalising: check GSC + Analytics daily for 14 days after any major redeploy.',
    },
  },
};

// ---------------------------------------------------------------------------
// Utility: build the ordered list of terms for the highlighter (longest first
// so multi-word terms match before their sub-words — e.g. "301 map" beats
// "301 redirect" and "core web vitals" beats "lcp").
// ---------------------------------------------------------------------------

export const GLOSSARY_TERMS_ORDERED = Object.keys(GLOSSARY).sort(
  (a, b) => b.length - a.length
);
